import { FC, useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Collapse,
  ListItemIcon,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Menu as MenuIcon, X } from 'lucide-react';
import { useSelector } from 'react-redux';
import './Sidebar.css';

interface RootState {
  user: {
    userDetails: {
      UserRole?: string;
      [key: string]: any;
    };
  };
}

interface MenuSection {
  text: string;
  path?: string;
  subItems?: MenuSection[];
}

const Sidebar: FC = () => {
  const { state: locationState, township, uuid } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({
    bids: true,
    reports: false,
    settings: false
  });

  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const [showSubmenu, setShowSubmenu] = useState<boolean>(false);

  const submenuItems: MenuSection[] = [
    {
      text: 'BIDS & PROPOSALS',
      subItems: [
        {
          text: 'Home',
          path: `/${locationState}/${township}/${uuid}/bid-portal/home`
        },
        {
          text: 'My Downloads',
          path: `/${locationState}/${township}/${uuid}/bid-portal/downloads`
        },
        {
          text: 'My Contacts',
          path: `/${locationState}/${township}/${uuid}/bid-portal/contacts`
        },
        {
          text: 'My Profile',
          path: `/${locationState}/${township}/${uuid}/bid-portal/profile`
        },
        {
          text: 'My Contracts',
          path: `/${locationState}/${township}/${uuid}/bid-portal/contracts`
        }
      ]
    }
  ];

  const menuStructure: MenuSection[] = [
    {
      text: 'BIDS & PROPOSALS',
      subItems: [
        {
          text: 'Home',
          path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/home`
        },
        {
          text: 'Modify Bids',
          path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/modifybids`
        },
        {
          text: 'Vendors',
          path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/vendors`
        },
        {
          text: 'Reports',
          subItems: [
            {
              text: 'Dashboard',
              path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/dashboard`
            },
            {
              text: 'Bid Summary',
              path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/bid-summary`
            },
            {
              text: 'Bid Notifications',
              path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/bid-notifications`
            },
            {
              text: 'Vendors',
              path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/report-vendors`
            },
            {
              text: 'Vendor Email Logs',
              path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/auth-notifications`
            },
            {
              text: 'User Activity',
              path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/user-activity`
            }
          ]
        },
        {
          text: 'Settings',
          subItems: [
            {
              text: 'Bid Categories',
              path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/bid-categories`
            },
            {
              text: 'Bid Departments',
              path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/bid-departments`
            },
            {
              text: 'Bid Status',
              path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/bid-status`
            },
            {
              text: 'Bid Type',
              path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/bid-type`
            },
            {
              text: 'Email Templates',
              path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/email-templates`
            },
            {
              text: 'Manage Users',
              path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/manage-users`
            }
          ]
        },
        {
          text: 'My Profile',
          path: `/${locationState}/${township}/${uuid}/bid-portal/Admin/my-profile`
        }
      ]
    }
  ];

  useEffect(() => {
    const isAdminRole =
      userDetails.UserRole === 'Admin' ||
      userDetails.UserRole === 'General Admin';
    setShowSubmenu(!isAdminRole);
  }, [userDetails]);

  // Close mobile drawer when route changes
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  const isSelected = (item: MenuSection): boolean => {
    if (item.path && location.pathname === item.path) {
      return true;
    }

    if (item.subItems) {
      return item.subItems.some((subItem) => isSelected(subItem));
    }

    return false;
  };

  useEffect(() => {
    const newOpenSections = { ...openSections };

    const checkAndOpenParent = (items: MenuSection[]) => {
      items.forEach((item) => {
        if (item.subItems) {
          const hasSelectedChild = item.subItems.some(
            (subItem) =>
              (subItem.path && location.pathname === subItem.path) ||
              (subItem.subItems && checkAndOpenParent([subItem]))
          );

          if (hasSelectedChild) {
            const sectionKey = item.text.toLowerCase().replace(/\s+/g, '');
            newOpenSections[sectionKey] = true;
          }
        }
      });

      return items.some((item) => isSelected(item));
    };

    checkAndOpenParent(menuStructure);
    checkAndOpenParent(submenuItems);
    setOpenSections(newOpenSections);
  }, [location.pathname]);

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const renderMenuItem = (item: MenuSection, depth = 0) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const sectionKey = item.text.toLowerCase().replace(/\s+/g, '');
    const selected = isSelected(item);
    const hasSelectedChild =
      hasSubItems && item.subItems?.some((subItem) => isSelected(subItem));

    const isBidsItem = item.text === 'BIDS & PROPOSALS';
    const textStyle = isBidsItem ? { marginLeft: '10px' } : {};

    const shouldRenderSubItems =
      userDetails.UserRole === 'Admin' ||
      userDetails.UserRole === 'General Admin' ||
      (!isBidsItem && Object.keys(userDetails).length > 0) ||
      (isBidsItem && showSubmenu);

    return (
      <div key={item.text}>
        <ListItem
          onClick={() =>
            hasSubItems && shouldRenderSubItems && toggleSection(sectionKey)
          }
          component={item.path ? Link : 'div'}
          to={item.path}
          className={`menu-item ${depth === 0 ? 'menu-section' : ''} ${depth > 0 ? 'submenu-item' : ''
            } ${selected ? 'selected' : ''} ${hasSelectedChild ? 'has-selected-child' : ''
            }`}
          style={{ paddingLeft: depth * 16 }}
        >
          <ListItemText>
            <Typography
              className={
                depth === 0 ? 'menu-section-text' : 'submenu-item-text'
              }
              style={textStyle}
            >
              {item.text}
            </Typography>
          </ListItemText>
          {hasSubItems && shouldRenderSubItems && (
            <ListItemIcon className="menu-icon">
              {openSections[sectionKey] ? (
                <ChevronDown size={20} />
              ) : (
                <ChevronRight size={20} />
              )}
            </ListItemIcon>
          )}
        </ListItem>

        {hasSubItems && shouldRenderSubItems && (
          <Collapse in={openSections[sectionKey]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.subItems.map((subItem) =>
                renderMenuItem(subItem, depth + 1)
              )}
            </List>
          </Collapse>
        )}
      </div>
    );
  };

  if (Object.keys(userDetails).length === 0) {
    return null;
  }

  const sidebarContent = (
    <Box className="sidebar-scroll">
      <List>
        {(showSubmenu ? submenuItems : menuStructure).map((item) =>
          renderMenuItem(item)
        )}
      </List>
    </Box>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && !mobileOpen && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1300,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'action.hover'
            }
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Mobile Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box'
            }
          }}
        >
          <Box className="sidebar-container">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <IconButton onClick={handleDrawerToggle}>
                <X />
              </IconButton>
            </Box>
            {sidebarContent}
          </Box>
        </Drawer>
      ) : (
        /* Desktop Sidebar */
        <Box className="sidebar-container">{sidebarContent}</Box>
      )}
    </>
  );
};

export default Sidebar;
